import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { comics, categories } from '@/mocks/comics';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
            <h1 className="text-2xl font-heading font-semibold text-foreground-900 mb-4">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查找的漫画不存在或已被移除。</p>
            <Link to="/" className="text-sm bg-primary-500 text-background-50 px-6 py-2.5 rounded-full whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryInfo = categories.find((c) => c.slug === comic.category);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩国漫画在线免费阅读`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.chapters[0]?.updateDate || comic.updateDate,
    dateModified: comic.updateDate,
    url: `https://koreanmanhwa.com/article/${comic.slug}`,
    publisher: {
      '@type': 'Organization',
      name: '韩国漫画网',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryInfo?.name || comic.category, href: `/category/${comic.category}` },
                { label: comic.title },
              ]}
            />

            <div className="flex flex-col lg:flex-row gap-8 py-6">
              <div className="lg:w-[300px] shrink-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden border border-background-200/70">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} - 韩国漫画`}
                    title={`${comic.title} - 韩国漫画在线阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900">
                  {comic.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  <span className="text-sm text-foreground-500">作者：{comic.author}</span>
                  <span className="text-sm text-foreground-500">|</span>
                  {comic.status === 'ongoing' ? (
                    <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded whitespace-nowrap">连载中</span>
                  ) : (
                    <span className="text-xs bg-accent-100 text-accent-700 px-2 py-0.5 rounded whitespace-nowrap">已完结</span>
                  )}
                  <span className="text-sm text-foreground-500">|</span>
                  <span className="text-sm text-foreground-500 flex items-center gap-1">
                    <i className="ri-star-fill text-yellow-500 w-4 h-4 flex items-center justify-center"></i>
                    {comic.rating}
                  </span>
                  <span className="text-sm text-foreground-500">|</span>
                  <span className="text-sm text-foreground-500">
                    {(comic.views / 10000).toFixed(0)}万阅读
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-1.5 mt-4">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="text-xs bg-secondary-100 text-secondary-800 px-2 py-0.5 rounded whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="mt-5">
                  <h3 className="text-sm font-medium text-foreground-900 mb-2">作品简介</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div className="flex items-center gap-3 mt-6">
                  <button className="bg-primary-500 text-background-50 text-sm px-6 py-2.5 rounded-full hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                    开始阅读 第1话
                  </button>
                  <button className="border border-background-300 text-foreground-600 text-sm px-6 py-2.5 rounded-full hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                    收藏作品
                  </button>
                </div>
              </div>
            </div>

            <div className="pb-14">
              <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-4">
                章节列表（共{comic.chapters.length}话）
              </h2>
              <div className="bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
                {comic.chapters.map((chapter, index) => (
                  <Link
                    key={chapter.id}
                    to={`/article/${comic.slug}`}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className={`flex items-center justify-between px-5 py-3.5 hover:bg-background-100 transition-colors duration-200 cursor-pointer ${
                      index < comic.chapters.length - 1 ? 'border-b border-background-200/70' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-foreground-800">
                        第{chapter.number}话
                      </span>
                      <span className="text-sm text-foreground-600">{chapter.title}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-foreground-400">{chapter.updateDate}</span>
                      <i className="ri-arrow-right-s-line text-foreground-300 w-4 h-4 flex items-center justify-center"></i>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div className="pb-14">
              <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-4">推荐阅读</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {comics
                  .filter((c) => c.id !== comic.id && c.category === comic.category)
                  .slice(0, 4)
                  .map((c) => (
                    <Link
                      key={c.id}
                      to={`/article/${c.slug}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="group block"
                    >
                      <div className="bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300">
                        <div className="relative aspect-[3/4] overflow-hidden">
                          <img
                            src={c.coverImage}
                            alt={`${c.title} - 韩国漫画`}
                            title={`${c.title} - 韩国漫画`}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                          />
                          <div className="absolute bottom-2 right-2 bg-background-950/70 text-background-50 text-xs px-2 py-0.5 rounded-full flex items-center gap-1 whitespace-nowrap">
                            <i className="ri-star-fill text-yellow-400 text-xs w-3 h-3 flex items-center justify-center"></i>
                            {c.rating}
                          </div>
                        </div>
                        <div className="p-3">
                          <p className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-500 transition-colors duration-200">
                            {c.title}
                          </p>
                          <p className="text-xs text-foreground-500 mt-1">{c.author}</p>
                        </div>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}